import {
  TemplateWidget,
  WidgetProduct,
  Product,
  BlogWidgetAttribute,
  BlogTemplateData,
  TopbarConfiguration,
  WidgetCarouselVariant,
  FiltersListingPreviewEntityType,
  WidgetRegionLink,
  WidgetCategoryLink,
} from '@/types'
import { EntityDetails } from '@/types'
import { InnerContent } from '@/components/BlogPageTemplate.styles'
import dynamic from 'next/dynamic'
import Container from '@/components/Container'

// Widgets
import Header from '@/components/widgets/Header'
import ListingPreview from '@/components/widgets/ListingPreview'
import FiltersListingPreview from '@/components/widgets/FiltersListingPreview'
import ProductCarousel from '@/components/ProductCarousel'
import CategoryLinks from '@/components/widgets/ThingsToDo'
import {
  Spacing,
  Heading,
  BlogDetails,
  ImageGridFour,
  CtaButton,
  PromoImage,
} from '@big-red-group/storefront-common-ui'
import ImageFan from '@/components/widgets/blog/ImageFan'
import ImageGridThree from '@/components/widgets/blog/ImageGridThree'
import ImageContent from '@/components/widgets/blog/ImageContent'
import Paragraph from '@/components/widgets/blog/Paragraph'
import BlogHeader from '@/components/widgets/blog/BlogHeader'
import BlogImage from '@/components/widgets/blog/BlogImage'
import BlogIntroduction from '@/components/widgets/blog/BlogIntroduction'
import RegionLinks from '@/components/widgets/RegionLinks'
import Editorial from '@/components/widgets/Editorial'
import Speedbump from '@/components/widgets/Speedbump'
import FreeThingsToDoCarousel from '@/components/widgets/FreeThingsToDoCarousel'
const Seo = dynamic(() => import('@/components/widgets/Seo'), { ssr: false })
import PopularCategories from '@/components/PopularCategories'
import BlogTiles from '@/components/widgets/BlogTiles'
import WaveDivider from '@/components/WaveDivider'
import HomeHero from '@/components/HomeHero'
import { HeroWrap, RegionsWrap } from '@/components/HomePageTemplate.styles'
import HomeRegions from '@/components/HomeRegions'
import HomeMemberHero from '@/components/HomeMemberHero'
import CategoryGrid from '@/components/CategoryGrid'
import WhyBookWithUs from '@/components/WhyBookWithUs'
import Campaign from '@/components/Campaign'
import FeaturedProductTabs from '@/components/widgets/FeaturedProductTabs'
import { getCustomBehaviour } from '@/helpers/WhitelabelHelper'
import HomeBanner from '@/components/widgets/HomeBanner'
import ProductCarouselTall from '@/components/ProductCarouselTall'
import ExozStories from '@/components/ExozStories'
import TopTenThings from '@/components/widgets/TopTenThings'
import SubcategoryBar from '@/components/widgets/SubcategoryBar'

// Converts an array of widget products to an array of products
export function toProduct(widgetProducts: WidgetProduct[]): Product[] {
  const products: Product[] = widgetProducts.map((widgetProduct: WidgetProduct, index: number) => {
    const product: Product = {
      id: index,
      title: widgetProduct.name,
      supplier: widgetProduct.supplierName!,
      canonicalRegionUrlSegment: '',
      fromPrice: widgetProduct.fromPrice,
      price: widgetProduct.retailPrice,
      levy: widgetProduct.levy,
      rating: widgetProduct.rating,
      //@ts-ignore
      region: widgetProduct.region ?? '',
      //@ts-ignore
      image: widgetProduct.imageUrl || widgetProduct.images[0]!,
      badges: widgetProduct.decorators,
      // TODO: lang
      url: widgetProduct.urlPath
        ? `/en${widgetProduct.urlPath}`
        : `/en/${widgetProduct.canonicalRegionUrlSegment}/${widgetProduct.urlSegment}`,
      inclusions: [],
      hotDeals: widgetProduct.hotDealMessage,
    }
    return product
  })
  return products
}

// Reordering Top Exp on Plps so it doesn't clash with ListingPreview below
export function customOrder(widgetProducts: any[]): any[] {
  const customOrder = [5, 3, 4, 0, 1, 2, 6, 7]
  const reorderedProducts = customOrder.filter((i) => i < widgetProducts?.length).map((i) => widgetProducts[i])
  return reorderedProducts
}

export const getFilterDataWidget = (widget: TemplateWidget) => {
  const { name } = widget

  if (name === 'category-links') {
    return widget.categories
  }

  if (name === 'region-links') {
    return widget.states
  }
}

export const getFiltersListingPreview = (
  widget: TemplateWidget,
  entityType: string,
  entityDetails: EntityDetails,
  regions: WidgetRegionLink[] | undefined,
  categories: WidgetCategoryLink[] | undefined,
  handlerKey?: string
) => {
  const { name: entityName } = entityDetails
  let listingTitle
  let slicedProducts
  let viewMore = true
  switch (entityType) {
    case 'REGION':
      slicedProducts = entityDetails.products
      listingTitle = `Explore ${entityName} experiences`
      break
    case 'STATE':
      slicedProducts = entityDetails.products
      listingTitle = `Explore ${entityName} experiences`
      // States cannnot be searched so disable the see all experiences option
      viewMore = false
      break
    case 'THINGS_TO_DO':
      slicedProducts = entityDetails.products
      listingTitle = `Explore ${entityName}`
      viewMore = true
      break
    default:
      slicedProducts = entityDetails.products
      listingTitle = `Top experiences`
  }

  return (
    <Container spacing="XXL">
      <FiltersListingPreview
        entityType={entityType as FiltersListingPreviewEntityType}
        products={slicedProducts}
        regions={regions}
        categories={categories}
        title={listingTitle}
        handlerKey={handlerKey}
        changeUrl
      />
    </Container>
  )
}

export const getWidget = (
  widget: TemplateWidget,
  entityType: string,
  entityDetails: EntityDetails,
  topbarConfiguration: TopbarConfiguration,
  isBrgAgent?: boolean,
  handlerKey?: string,
  region?: string,
  partnerCode?: string | undefined,
  routerQuery?: any
) => {
  const { name } = widget
  const { name: entityName } = entityDetails
  let searchPath = ''
  switch (entityType) {
    case 'REGION':
      searchPath = `${entityDetails.urlSegment}`
      break
    case 'HOME':
      searchPath = ''
      break
    case 'THINGS_TO_DO':
      searchPath = `${entityDetails.urlSegment}`
      break
    // Router query is passed down because subcats cannot be searched and the category is not availble in the data
    case 'SUBCATEGORY':
      searchPath = `${region}/${routerQuery.part2}`
      break
    default:
      searchPath = `${region}/${entityDetails.urlSegment}`
  }

  if (name === 'contextual-subcategory-links') {
    switch (entityType) {
      case 'CATEGORY':
        return <SubcategoryBar subcategories={entityDetails.subcategories} parentEntityName={entityName} />
      case 'SUBCATEGORY':
        return (
          <SubcategoryBar
            selected={entityName}
            subcategories={entityDetails.subcategories}
            parentEntityName={entityDetails?.categories?.find((cat) => cat.id === entityDetails.categoryId)?.name!}
          />
        )
      case 'THINGS_TO_DO':
        return <SubcategoryBar selected={entityName} parentEntityName={entityName} useDefault={true} />
    }
  }

  if (name === 'home-banner') {
    const heroWaveDividerStyle = { height: '70%', width: '160%', left: '-20%' }
    return (
      <>
        <HeroWrap data-mcp-id="home-wrap">
          {partnerCode !== 'virgin' && <WaveDivider curveSize="medium" style={heroWaveDividerStyle} fill="#fac532" />}
          <Container spacing="XXL">
            <HomeHero
              image={widget.image}
              title={widget.title}
              subtitle={widget.subtitle}
              destinations={topbarConfiguration?.destinations}
            />
            {partnerCode === 'virgin' && <Spacing size="XXL" />}
            {getCustomBehaviour(partnerCode, { renderHomePromisePoints: true })}
          </Container>
        </HeroWrap>
      </>
    )
  }

  if (name === 'home-banner-minimal') {
    return (
      <>
        <HomeBanner title={widget.title} subtitle={widget.subtitle} banners={widget.banners} />
        <Spacing size="XXS" />
        {getCustomBehaviour(handlerKey, { renderHomePromisePoints: true })}
      </>
    )
  }

  if (name === 'featured-product-tabs') {
    return (
      <Container spacing="XXL">
        <Spacing size="XXL" />
        <FeaturedProductTabs tabs={widget.tabs} title={widget.title} />
        <Spacing size="XXL" />
      </Container>
    )
  }

  if (name === 'region-carousel') {
    const regionsWaveDividerStyle = { height: '70%', width: '150%', left: '-10%', transform: 'scaleX(-1) scaleY(-1)' }
    return (
      <RegionsWrap data-mcp-id="regions-wrap">
        {isBrgAgent && <WaveDivider curveSize="medium" style={regionsWaveDividerStyle} fill="#FAC532" topDivider />}
        <HomeRegions regions={widget.regions} label={widget.label} />
        <Spacing size="XXXL" />
      </RegionsWrap>
    )
  }

  if (name === 'destination-promo') {
    return (
      <>
        <HomeMemberHero image={widget.image} location={widget.destination} title={widget.heading} />
        <Spacing size="XXXL" />
      </>
    )
  }

  if (name === 'category-grid') {
    return (
      <>
        <CategoryGrid categories={widget.categories} label={widget.label} />
        <Spacing size="XXXL" />
      </>
    )
  }

  if (name === 'trust-factors') {
    return (
      <>
        <WhyBookWithUs />
        <Spacing size="XXXL" />
      </>
    )
  }

  if (name === 'cta-promo-banner') {
    return (
      <>
        <Campaign fullWidth title={widget.title} buttonLabel={widget.cta} url={widget.link} image={widget.image} />
        <Spacing size="XXXL" />
      </>
    )
  }

  if (name === 'hero-image') {
    return (
      <Header
        image={widget.imageUrl}
        href={searchPath}
        // TODO adding entity.name as per the current things to do implementation - but would widget.title be a better choice?
        title={entityDetails?.heading || entityDetails.name}
        description={entityDetails?.summary}
        variant={
          entityType === 'CATEGORY' || entityType === 'SUBCATEGORY' || entityType === 'THINGS_TO_DO' ? 'square' : 'full'
        }
      />
    )
  }

  if (name === 'category-links') {
    return (
      <Container spacing="XXL">
        <CategoryLinks
          categories={widget.categories}
          region={entityDetails.urlSegment}
          seeMore={entityType == 'REGION' ? false : true}
        />
      </Container>
    )
  }

  if (name === 'region-links') {
    return (
      <Container spacing="XXL">
        <RegionLinks states={widget.states} region={entityDetails.urlSegment} title={widget.title} />
      </Container>
    )
  }

  if (name === 'campaign-promo') {
    return (
      <Container spacing="XXL">
        <Editorial image={widget.image} title={widget.title} description={widget.description} link={widget.link} />
      </Container>
    )
  }

  if (name === 'speedbump') {
    return (
      <Container spacing="XXL">
        <Speedbump image={widget.image} link={widget.link} />
      </Container>
    )
  }

  if (name === 'product-listing') {
    let listingTitle
    let slicedProducts
    let viewMore = true
    switch (entityType) {
      case 'REGION':
        slicedProducts = entityDetails.products.slice(0, 12)
        listingTitle = `Explore ${entityName} experiences`
        break
      case 'STATE':
        slicedProducts = entityDetails.products
        listingTitle = `Explore ${entityName} experiences`
        // States cannnot be searched so disable the see all experiences option
        viewMore = false
        break
      case 'THINGS_TO_DO':
        slicedProducts = entityDetails.products
        listingTitle = `Explore ${entityName}`
        viewMore = true
        break
      default:
        slicedProducts = entityDetails.products.slice(0, 12)
        listingTitle = `Top experiences`
    }

    return (
      <Container spacing="XXL">
        <ListingPreview
          isRecommended={false}
          title={listingTitle}
          products={slicedProducts}
          viewMore={viewMore ? { label: 'See all experiences', link: `/en/search/${searchPath}` } : undefined}
          handlerKey={handlerKey}
          speedBumpTile={false}
          smallMobileTiles
          toggleProductListViewed={true}
        />
      </Container>
    )
  }

  if (
    [
      'dynamic-product-carousel',
      'static-product-carousel',
      'region-product-carousel',
      'category-product-carousel',
      'contextual-dynamic-product-carousel',
    ].includes(name)
  ) {
    const productWidget = widget as {
      name: string
      label: string
      products: WidgetProduct[]
      variant: WidgetCarouselVariant
    }
    let title
    switch (entityType) {
      case 'REGION':
      case 'THINGS_TO_DO':
        title = 'Top experiences'
        break
      default:
        title = productWidget.label
    }
    const regionPlp =
      entityType === 'REGION' ||
      entityType === 'CATEGORY' ||
      entityType === 'SUBCATEGORY' ||
      entityType === 'THINGS_TO_DO'
    const products = regionPlp ? customOrder(productWidget.products) : productWidget.products
    if ((!regionPlp && productWidget?.products?.length >= 4) || (regionPlp && entityDetails?.products?.length > 8)) {
      return (
        <>
          {productWidget.variant === 'large' ? (
            <ProductCarouselTall products={toProduct(products)} label={productWidget.label} />
          ) : (
            <ProductCarousel
              numberedList
              title={title}
              products={toProduct(products)}
              handlerKey={handlerKey}
              noSpacingTop={regionPlp}
            />
          )}
          <Spacing size="XXXL" />
        </>
      )
    }
  }

  if (name === 'page-description') {
    let seoTitle
    let description
    switch (entityType) {
      case 'REGION':
        seoTitle = `Explore the best of ${entityName}`
        description = entityDetails.description
        break
      case 'CATEGORY':
        seoTitle = `Discover ${entityName}`
        description = entityDetails.description
        break
      case 'SUBCATEGORY':
        seoTitle = `Discover the best of ${entityName}`
        description = entityDetails.description
        break
      case 'HOME':
        seoTitle = ``
        description = widget.text
        break
      default:
        seoTitle = `Discover ${entityName}`
        description = entityDetails.description
    }
    return (
      <Container spacing="XXL">
        <Seo title={seoTitle} description={description} />
      </Container>
    )
  }

  if (name === 'heading') {
    return (
      <Container spacing="XXL">
        <Heading size="XL" spacing={false}>
          {widget.title}
        </Heading>
        <Spacing size="M" />
      </Container>
    )
  }

  if (name === 'content-block-left') {
    return (
      <Container spacing="XXL">
        <ImageContent image={widget.image} heading={widget.heading} text={widget.text} reverse={false} />
        <Spacing size="XXXL" />
      </Container>
    )
  }

  if (name === 'content-block-right') {
    return (
      <Container spacing="XXL">
        <ImageContent image={widget.image} heading={widget.heading} text={widget.text} reverse={true} />
        <Spacing size="XXXL" />
      </Container>
    )
  }

  if (name === 'paragraph') {
    return (
      <Container spacing="S">
        <InnerContent>
          <Paragraph text={widget.text} />
          <Spacing size="XXXL" />
        </InnerContent>
      </Container>
    )
  }

  if (name === 'free-products-carousel') {
    return (
      <>
        <FreeThingsToDoCarousel
          title={`Free things to do ${entityName}`}
          products={widget.products}
          handlerKey={handlerKey}
          region={entityDetails?.urlSegment}
        />
      </>
    )
  }

  if (isBrgAgent && name === 'top-ten-things') {
    return <>{widget.things && <TopTenThings widget={widget} entityName={entityName} />}</>
  }

  if (name === 'contextual-category-carousel') {
    return (
      <Container spacing="XXL">
        <PopularCategories categories={widget.categories} region={entityDetails.urlSegment} />
      </Container>
    )
  }

  if (isBrgAgent && name === 'blog-pages-carousel') {
    return (
      <>
        {widget.variant === 'recent' ? (
          <ExozStories stories={widget.blogPages} />
        ) : (
          <Container>
            <BlogTiles blogPages={widget.blogPages} />
          </Container>
        )}
      </>
    )
  }
}

export const getBlogWidget = (templateData: BlogTemplateData, nextWidget: BlogTemplateData | undefined) => {
  const { name } = templateData
  const attributes = 'attributes' in templateData ? templateData.attributes : []
  if (name === 'blog-header') {
    return (
      <BlogHeader
        title={getBlogAttribute(attributes, 'title') || ''}
        subtitle={getBlogAttribute(attributes, 'subtitle') || ''}
      />
    )
  }

  if (name === 'image') {
    return (
      <>
        <BlogImage image={getBlogAttribute(attributes, 'image') || ''} />
        <Spacing size="XXXL" />
      </>
    )
  }

  if (name === 'introduction') {
    return (
      <>
        <InnerContent>
          <BlogIntroduction text={getBlogAttribute(attributes, 'text') || ''} />
          <Spacing size="XXXL" />
        </InnerContent>
      </>
    )
  }

  if (name === 'heading') {
    return (
      <>
        <Heading size="XL" spacing={false}>
          {getBlogAttribute(attributes, 'text')}
        </Heading>
        <Spacing size="M" />
      </>
    )
  }

  if (name === 'sub-heading') {
    return (
      <InnerContent>
        <Heading size="L" spacing={false}>
          {getBlogAttribute(attributes, 'text')}
        </Heading>
        <Spacing size="M" />
      </InnerContent>
    )
  }

  if (name === 'paragraph') {
    const useSmallSpacing = nextWidget?.name === 'paragraph'
    return (
      <InnerContent>
        <Paragraph text={getBlogAttribute(attributes, 'text') || ''} />
        <Spacing size={useSmallSpacing ? 'M' : 'XXXL'} />
      </InnerContent>
    )
  }

  if (name === 'promo-image') {
    return (
      <>
        <PromoImage
          image={getBlogAttribute(attributes, 'image') || ''}
          link={getBlogAttribute(attributes, 'link') || ''}
          cta={getBlogAttribute(attributes, 'cta') || ''}
          buttonSize="S"
        >
          <Heading size="XXL" style={{ color: 'white' }}>
            {getBlogAttribute(attributes, 'heading') || ''}
          </Heading>
        </PromoImage>
        <Spacing size="XXXL" />
      </>
    )
  }

  if (name === 'blog-details') {
    return (
      <InnerContent>
        <BlogDetails
          author={getBlogAttribute(attributes, 'author') || ''}
          date={getBlogAttribute(attributes, 'date') || ''}
          readtime={getBlogAttribute(attributes, 'readtime') || ''}
        />
        <Spacing size="M" />
      </InnerContent>
    )
  }

  if (name === 'image-fan') {
    return (
      <>
        <ImageFan
          first={getBlogAttribute(attributes, 'first') || ''}
          second={getBlogAttribute(attributes, 'second') || ''}
          third={getBlogAttribute(attributes, 'third') || ''}
        />
        <Spacing size="XXXL" />
      </>
    )
  }

  if (name === 'image-grid-four') {
    return (
      <>
        <ImageGridFour
          first={getBlogAttribute(attributes, 'first') || ''}
          second={getBlogAttribute(attributes, 'second') || ''}
          third={getBlogAttribute(attributes, 'third') || ''}
          fourth={getBlogAttribute(attributes, 'fourth') || ''}
        />
        <Spacing size="XXXL" />
      </>
    )
  }

  if (name === 'image-grid-three') {
    return (
      <>
        <ImageGridThree
          first={getBlogAttribute(attributes, 'first') || ''}
          second={getBlogAttribute(attributes, 'second') || ''}
          third={getBlogAttribute(attributes, 'third') || ''}
        />
        <Spacing size="XXXL" />
      </>
    )
  }

  if (name === 'content-block-left') {
    return (
      <>
        <ImageContent
          image={getBlogAttribute(attributes, 'image') || ''}
          heading={getBlogAttribute(attributes, 'heading') || ''}
          text={getBlogAttribute(attributes, 'text') || ''}
          reverse={false}
          truncateText={true}
        />
        <Spacing size="XXXL" />
      </>
    )
  }

  if (name === 'content-block-right') {
    return (
      <>
        <ImageContent
          image={getBlogAttribute(attributes, 'image') || ''}
          heading={getBlogAttribute(attributes, 'heading') || ''}
          text={getBlogAttribute(attributes, 'text') || ''}
          reverse={true}
          truncateText={true}
        />
        <Spacing size="XXXL" />
      </>
    )
  }

  if (name === 'cta-button') {
    return (
      <InnerContent>
        <CtaButton
          link={getBlogAttribute(attributes, 'link') || ''}
          text={getBlogAttribute(attributes, 'text') || ''}
        />
        <Spacing size="XXXL" />
      </InnerContent>
    )
  }

  if (['static-product-carousel', 'region-product-carousel', 'category-product-carousel'].includes(name)) {
    const data = templateData as Extract<BlogTemplateData, { name: 'static-product-carousel' }>
    return (
      <>
        <ProductCarousel title={data.label} products={toProduct(data.products)} numberedList />
        <Spacing size="XXXL" />
      </>
    )
  }
}

export function getBlogAttribute(attributes: BlogWidgetAttribute[] = [], attributeName: string): string | null {
  return attributes?.find((attribute) => attribute.name === attributeName)?.value || null
}

export const generateHtmlSafeId = (id: string | number) => {
  const formattedId = String(id)
    .replace(/[^A-Z0-9]+/gi, '-')
    .toLowerCase()
  return formattedId && !isNaN(Number(formattedId.charAt(0))) ? `_${formattedId}` : formattedId
}
